import React, { useContext, useEffect, useState } from 'react';
import * as S from '../styled';
import Form from '../../..';
import { ExportContext } from '../../../../../provider/ExportProvider';
import {
  formatBRL,
  formatCents,
  formatCompleteBRL,
  formatCompleteCents,
  formatNumber,
} from '../../../../../store/masks';
import { maskFieldDate, unmaskFieldDate } from '../../utils';
import TextArea from '../../../../TextArea';
import { useAbility } from '../../../../Hooks/useAbility';

export default function TabFormInvoiceUpdate() {
  const {
    getInvoice,
    setInvoice,
    getFormMessage,
    selectCompanies,
    setCompanyFilterField,
    filterSelectCompanies,
    situations,
    labels,
    productItems,
    productLoading,
    filterProducts,
    invoiceGroupItems,
    invoiceGroups,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.invoice;
  const { ability } = useAbility();

  const getForm = <T extends FlattenKeys<InvoiceForm>>(field: T) =>
    getInvoice(`forms.invoices.update.${field}`);

  const setForm = <
    T extends FlattenKeys<InvoiceForm>,
    V extends DeepType<InvoiceState, `forms.invoices.update.${T}`>,
  >(
    field: T,
    value: V,
  ) => setInvoice(`forms.invoices.update.${field}`, value);

  const blurField = (field: keyof InvoiceForm) => () => {
    setInvoice(`blur.invoices.update.${field}`, true);
  };

  const messages = getFormMessage('invoices.update');

  const getProduct = () => {
    const { resume } = getInvoice('forms.invoices.update.product');
    return `${resume}`;
  };

  const setProduct = (value: string) => {
    setInvoice('forms.invoices.update.product.resume', value);
  };

  const [companyLoading, setCompanyLoading] = useState(false);
  const filterCompanies = () => {
    setCompanyLoading(true);
    filterSelectCompanies().finally(() => {
      setCompanyLoading(false);
    });
  };

  const form = getInvoice('forms.invoices.update');
  const {
    invoiceQuantity,
    unitCostReal,
    unitCostTotal,
    unitSale,
    invoiceTaxes,
    realValue,
  } = form;

  const taxValue = invoiceTaxes.reduce((acc, acv) => acc + acv.value, 0);

  const updateTaxes = (taxValue: number) => {
    const totalCost = realValue + taxValue;
    const unitCostTotal = invoiceQuantity
      ? Math.round(totalCost / invoiceQuantity)
      : 0;
    const unitProfit = unitSale - unitCostTotal;
    setInvoice('forms.invoices.update', {
      ...form,
      unitCostTotal,
      totalCost,
      unitProfit,
      totalProfit: unitProfit * invoiceQuantity,
    });
  };

  const locked = getForm('locked');

  const group = getForm('group')

  useEffect(() => {
    updateTaxes(taxValue);
    if (!selectedGroupId && (!group.id && invoiceGroups.length > 0))
      setForm('group', invoiceGroups[0]);
  }, [invoiceGroups, selectedGroupId]);

  return (
    <S.Body>
      <S.Grid>
        <Form.Input.Dropdown
          label="Companhia"
          items={selectCompanies.map(({ id, name }) => ({ id, name }))}
          value={getForm('company.name')}
          onChange={(e) => {
            setForm('company.name', e.target.value);
            setCompanyFilterField('name')(e);
          }}
          onDebouncedChange={filterCompanies}
          error={messages.company}
          onBlur={blurField('company')}
          hasClearItem
          onFocus={() => {
            setCompanyFilterField('name')({ target: { value: '' } });
          }}
          onSelected={({ id }) => {
            setForm('company.id', +(id || 0));
          }}
          loading={companyLoading}
          disabled={locked || ability?.cannot('update', 'Invoice', 'company')}
        />

        <Form.Input.Dropdown
          label="Produto"
          value={getProduct()}
          onChange={(e) => setProduct(e.target.value)}
          items={productItems}
          error={messages.product}
          onBlur={blurField('product')}
          onSelected={(item) => {
            setInvoice('forms.invoices.update.product', {
              code: item.data.code,
              resume: item.data.resume,
            });
          }}
          loading={productLoading}
          hasClearItem
          onDebouncedChange={(e) => {
            filterProducts(e.target.value);
          }}
          disabled={locked || ability?.cannot('update', 'Invoice', 'product')}
        />

        <Form.Input.DatePicker
          label="Data"
          value={maskFieldDate(getForm('date'))}
          onChange={(e) => setForm('date', unmaskFieldDate(e.target.value))}
          error={messages.date}
          onBlur={blurField('date')}
          disabled={locked || ability?.cannot('update', 'Invoice', 'date')}
        />

        <Form.Input
          label="CRT"
          value={getForm('crt')}
          onChange={(e) => setForm('crt', e.target.value)}
          error={messages.crt}
          onBlur={blurField('crt')}
          disabled={locked || ability?.cannot('update', 'Invoice', 'crt')}
        />

        <Form.Input
          label="Nota"
          value={getForm('invoiceNumber')}
          onChange={(e) => setForm('invoiceNumber', e.target.value)}
          error={messages.invoiceNumber}
          onBlur={blurField('invoiceNumber')}
          disabled={
            locked || ability?.cannot('update', 'Invoice', 'invoiceNumber')
          }
        />

        <Form.Input
          label="Valor NF"
          value={getForm('invoiceValue')}
          onChange={(e) => setForm('invoiceValue', +e.target.value)}
          mask={formatBRL}
          unmask={formatCents}
          error={messages.invoiceValue}
          onBlur={blurField('invoiceValue')}
          disabled={
            locked || ability?.cannot('update', 'Invoice', 'invoiceValue')
          }
        />

        <Form.Input
          label="Valor real"
          value={getForm('realValue')}
          onChange={(e) => {
            const realValue = +e.target.value;
            const totalCost = realValue + taxValue;
            const unitCostReal = invoiceQuantity
              ? Math.round(realValue / invoiceQuantity)
              : 0;
            const unitCostTotal = invoiceQuantity
              ? Math.round(totalCost / invoiceQuantity)
              : 0;
            const unitProfit = unitSale - unitCostTotal;
            setInvoice('forms.invoices.update', {
              ...form,
              unitCostReal,
              unitCostTotal,
              totalCost,
              unitProfit,
              totalProfit: unitProfit * invoiceQuantity,
              realValue,
            });
          }}
          mask={formatBRL}
          unmask={formatCents}
          error={messages.realValue}
          disabled
        />

        <Form.Input
          label="Quantidade"
          value={getForm('invoiceQuantity')}
          onChange={(e) => {
            const invoiceQuantity = +e.target.value;
            const realValue = invoiceQuantity * unitCostReal;
            const totalCost = realValue + taxValue;
            const unitCostTotal = invoiceQuantity
              ? Math.round(totalCost / invoiceQuantity)
              : 0;
            const unitProfit = unitSale - unitCostTotal;
            setInvoice('forms.invoices.update', {
              ...form,
              unitCostTotal,
              invoiceQuantity,
              unitProfit,
              totalProfit: unitProfit * invoiceQuantity,
              realValue,
              totalCost,
            });
          }}
          error={messages.invoiceQuantity}
          onBlur={blurField('invoiceQuantity')}
          mask={formatNumber}
          unmask={formatNumber}
          disabled={
            locked || ability?.cannot('update', 'Invoice', 'invoiceQuantity')
          }
        />

        <Form.Input
          label="Custo unitário real"
          value={getForm('unitCostReal')}
          onChange={(e) => {
            const unitCostReal = +e.target.value;
            const realValue = unitCostReal * invoiceQuantity;
            const totalCost = realValue + taxValue;
            const unitCostTotal = invoiceQuantity
              ? Math.round(totalCost / invoiceQuantity)
              : 0;
            const unitProfit = unitSale - unitCostTotal;

            setInvoice('forms.invoices.update', {
              ...form,
              unitCostReal,
              unitCostTotal,
              totalCost,
              unitProfit,
              totalProfit: unitProfit * invoiceQuantity,
              realValue,
            });
          }}
          mask={formatBRL}
          unmask={formatCents}
          error={messages.unitCostReal}
          disabled={
            locked || ability?.cannot('update', 'Invoice', 'unitCostReal')
          }
        />

        <Form.Input
          label="Custo total"
          value={getForm('totalCost')}
          onChange={(e) => {
            setForm('totalCost', +e.target.value);
          }}
          mask={formatBRL}
          unmask={formatCents}
          disabled
        />

        <Form.Input
          label="Custo unitário total"
          value={getForm('unitCostTotal')}
          onChange={(e) => setForm('unitCostTotal', +e.target.value)}
          mask={formatBRL}
          unmask={formatCents}
          disabled
        />

        <Form.Input
          label="Venda unitária"
          value={getForm('unitSale')}
          onChange={(e) => {
            const unitSale = +e.target.value;
            const unitProfit = unitSale - unitCostTotal;
            setInvoice('forms.invoices.update', {
              ...form,
              unitSale: unitSale,
              unitProfit,
              totalProfit: unitProfit * invoiceQuantity,
            });
          }}
          mask={formatBRL}
          unmask={formatCents}
          error={messages.unitSale}
          disabled={locked || ability?.cannot('update', 'Invoice', 'unitSale')}
        />

        <Form.Input
          label="Lucro total"
          value={getForm('totalProfit')}
          onChange={(e) => setForm('totalProfit', +e.target.value)}
          mask={formatCompleteBRL}
          unmask={formatCompleteCents}
          disabled
        />

        <Form.Input
          label="Lucro unitário"
          value={getForm('unitProfit')}
          onChange={(e) => setForm('unitProfit', +e.target.value)}
          mask={formatCompleteBRL}
          unmask={formatCompleteCents}
          disabled
        />

        <Form.Input.Dropdown
          label="Situação"
          items={situations}
          value={
            situations.find(({ data }) => data === getForm('status'))?.name ??
            ''
          }
          error={messages.status}
          onBlur={blurField('status')}
          onSelected={(item) => {
            setForm('status', item.data as InvoiceStatus);
          }}
          disabled={locked || ability?.cannot('update', 'Invoice', 'status')}
        />

        <Form.Input.Dropdown
          label="Etiquetado"
          items={labels}
          value={
            labels.find(({ data }) => data === getForm('labeled'))?.name ?? ''
          }
          error={messages.labeled}
          onBlur={blurField('labeled')}
          onSelected={(item) => {
            setForm('labeled', item.data as boolean);
          }}
          disabled={locked || ability?.cannot('update', 'Invoice', 'labeled')}
        />

        {ability?.can('update', 'Invoice', 'groupId') && !selectedGroupId && (invoiceGroupItems.length > 1) ? (
          <Form.Input.Dropdown
            label="Grupo"
            items={invoiceGroupItems}
            value={
              invoiceGroupItems.find(({ id }) => id === getForm('group')?.id)?.name ??
              ''
            }
            error={messages.group}
            onBlur={blurField('group')}
            onSelected={(item) => {
              setForm('group', item.data);
            }}
            disabled={invoiceGroupItems.length === 1}
          />
        ) : null}

      </S.Grid>
      <div style={{ padding: '0 20px' }}>
        <TextArea
          label="Observação"
          value={getForm('observation')}
          onChange={(e) => setForm('observation', e.target.value)}
          disabled={
            locked || ability?.cannot('update', 'Invoice', 'observation')
          }
        />
      </div>
    </S.Body>
  );
}
